import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import slugify from 'slugify';

import Layout, { ContentSection } from '~components/Layout';
import LastUpdated from '~components/common/LastUpdated';
import { HTMLContent } from '~components/Content';
import TableLayout from '~components/InfoPage/TableLayout';
import ListLayout from '~components/InfoPage/ListLayout';
import miniTools from '~components/InfoPage/MiniTools';
import Button from '~components/common/Button';
import HeaderButtonMenu from '~components/home/HeaderButtonMenu';
import TableLayoutWithGroup from '../components/InfoPage/TableLayoutWithGroup';

function InfoPage({ data, pageContext }) {
  const stateInfos = data.allVoteAmericaStateInformation.group;
  const overrides = data.allVoteAmericaOverride.group;
  const infoFields = data.allVoteAmericaField.nodes;
  const allModifiedDates = stateInfos.reduce(
    (arr, state) => arr.concat(state.nodes),
    []
  );

  const { infoSlug } = pageContext;
  const { html, frontmatter } = data.markdownRemark;

  function MiniTool() {
    return null;
  }
  if (frontmatter.miniTool) {
    // eslint-disable-next-line no-func-assign
    MiniTool = miniTools[frontmatter.miniTool];
  }

  const sections = frontmatter.sections ? frontmatter.sections : [frontmatter];

  return (
    <Layout
      title={frontmatter.title}
      description={frontmatter.description}
      image={frontmatter.image}
    >
      <ContentSection
        width={frontmatter.pageLayout === 'list' ? 'mid' : 'wide'}
      >
        <div className='mx-auto'>
          <LastUpdated array={allModifiedDates} />
          <h1>{frontmatter.headline}</h1>
          {frontmatter.displayToolButtons && (
            <div className='mb-7'>
              <HeaderButtonMenu tools={['verify', 'register', 'locate']} />
            </div>
          )}
          <MiniTool
            frontmatter={frontmatter}
            infoSlug={infoSlug}
            stateInfos={stateInfos}
            infoFields={infoFields}
            overrides={overrides}
          />
          <HTMLContent className='content' content={html} />
          {frontmatter.buttonText && frontmatter.buttonLink && (
            <Button
              to={
                frontmatter.buttonLink.endsWith('/')
                  ? frontmatter.buttonLink
                  : `${frontmatter.buttonLink}/`
              }
              title={frontmatter.buttonText}
            >
              {frontmatter.buttonText}
            </Button>
          )}
        </div>
        {sections.length > 1 && (
          <ul>
            {frontmatter.sections.map((section) => (
              <li key={section.name}>
                <a
                  href={`#${slugify(section.name.toLowerCase())}`}
                  key={slugify(section.name.toLowerCase())}
                  title={section.name}
                >
                  {section.name}
                </a>
              </li>
            ))}
          </ul>
        )}
        <div className='space-y-10'>
          {sections.map((section) => (
            <>
              {section.pageLayout === 'list' && (
                <ListLayout
                  frontmatter={section}
                  infoSlug={section.infoSlug}
                  stateInfos={stateInfos}
                  infoFields={infoFields}
                  title={section.name}
                />
              )}
              {section.pageLayout === 'table' && (
                <TableLayout
                  frontmatter={section}
                  infoSlug={section.infoSlug}
                  stateInfos={stateInfos}
                  infoFields={infoFields}
                  overrides={overrides}
                  title={section.name}
                />
              )}
              {section.pageLayout === 'table-with-group' && (
                <TableLayoutWithGroup
                  frontmatter={section}
                  infoSlug={section.infoSlug}
                  stateInfos={stateInfos}
                  infoFields={infoFields}
                  overrides={overrides}
                  title={section.name}
                />
              )}
            </>
          ))}
        </div>
      </ContentSection>
    </Layout>
  );
}

InfoPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object.isRequired,
    allVoteAmericaStateInformation: PropTypes.shape({
      group: PropTypes.arrayOf(
        PropTypes.shape({
          fieldValue: PropTypes.string,
          nodes: PropTypes.arrayOf(
            PropTypes.shape({
              html: PropTypes.string,
              modified_at: PropTypes.string,
              text: PropTypes.string,
            })
          ),
        })
      ),
    }),
    allVoteAmericaField: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          description: PropTypes.string,
          slug: PropTypes.string,
        })
      ),
    }),
    allVoteAmericaOverride: PropTypes.shape({
      group: PropTypes.arrayOf(
        PropTypes.shape({
          fieldValue: PropTypes.string,
          nodes: PropTypes.arrayOf(
            PropTypes.shape({
              value: PropTypes.string,
              region: PropTypes.shape({
                name: PropTypes.string,
              }),
              field: PropTypes.shape({
                slug: PropTypes.string,
              }),
            })
          ),
        })
      ),
    }),
  }),
  pageContext: PropTypes.shape({
    id: PropTypes.string,
    infoSlug: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default InfoPage;

export const pageQuery = graphql`
  query InfoPage($id: String!, $infoSlug: [String]) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        pageLayout
        headline
        description
        title
        image
        stateLink
        stateTitleAppend
        stateListAppendText
        stateListAppendTextHeader
        stateListAppendLink
        stateListAppendLinkIsButton
        infoSlugHeaders
        infoSlug
        buttonText
        buttonLink
        stateListGrouping
        headers
        rowRenderer
        listRenderer
        miniTool
        displayToolButtons
        sections {
          name
          pageLayout
          stateListGrouping
          rowRenderer
          headers
          infoSlug
        }
      }
    }
    allVoteAmericaStateInformation(
      filter: { field_type: { in: $infoSlug } }
      sort: { state_name: ASC }
    ) {
      group(field: { state_name: SELECT }) {
        fieldValue
        nodes {
          html
          bool_value
          multiselect_value
          modified_at
          footnotes
          field_type
          text
        }
      }
    }
    allVoteAmericaField(filter: { slug: { in: $infoSlug } }) {
      nodes {
        description
        slug
        field_format
      }
    }
    allVoteAmericaOverride {
      group(field: { state: SELECT }) {
        fieldValue
        nodes {
          field {
            slug
          }
          region {
            name
          }
          value
        }
      }
    }
  }
`;
